import { useRouter } from "@/_router";
import { useTranslation } from "react-i18next";
import { desktop, runtime, useAppStore } from "@/controller";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  memo,
} from "react";
import { useShallow } from "zustand/react/shallow";
import Settings from "./settings";
import {
  CrownIcon,
  SettingsIcon,
  MicrophoneIcon,
  UploadIcon,
  TrashIcon,
  TagIcon,
  CheckIcon,
  SelectIcon,
  CloseIcon,
  WarningIcon,
  SearchIcon,
  DownloadIcon,
  CursorIcon,
  ArrowDownIcon,
  AudioWaveformIcon,
} from "@/components/Icons";
import { useUISlice } from "@/models/useUISlice";
import TagDots from "@/components/TagDots";
import TagFilterBar from "@/components/TagFilterBar";
import { Transcript, Tag } from "@/types";
import { getTranscriptDirection, type ContentDirection } from "@/text-direction";
import Container from "@/components/Container";
import { logClientEvent } from "@/telemetry";
import { actions } from "@/controller";
import { exportTranscriptArchive } from "@/transcript-export";
import { createActionError } from "@/client-error";
import toast from "react-hot-toast";
import {
  HomeBillingButton,
  HomeNativeFeatures,
  NativeHomeScanButton,
  HomeNextStep,
  useHomeBootstrap,
} from "@/components/home/HomePlatformCapabilities";
import { isDesktopDictationSetupComplete } from "@/dictation-interactions";
import { useSharedDesktopHost } from "@/models/desktop-host-context";
import {
  isUploadEntry,
  UPLOAD_ENTRY,
} from "@/onboarding-interactions";

const isPendingTranscript = (transcript: Transcript) =>
  transcript.status === "transcription_started" ||
  transcript.status === "upload_started" ||
  transcript.status === "upload_completed";

const startPendingTranscriptPolls = () => {
  for (const transcript of useAppStore.getState().transcripts) {
    if (isPendingTranscript(transcript)) {
      actions.transcript.pollStatus(transcript.id);
    }
  }
};

const Home = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const desktopHost = useSharedDesktopHost();
  const {
    transcripts,
    isSubscribed,
    userData,
    transcriptsNextCursor,
    selectedTagFilter,
  } = useAppStore(
    useShallow((state) => ({
      transcripts: state.transcripts,
      isSubscribed: state.isSubscribed,
      userData: state.userData,
      transcriptsNextCursor: state.transcriptsNextCursor,
      selectedTagFilter: state.selectedTagFilter,
    })),
  );
  const alertWidgetCreateTranscriptFromFileImport = useUISlice(
    (state) => state.alertWidgetCreateTranscriptFromFileImport,
  );
  const alertPaywallVisible = useUISlice((state) => state.paywallVisible);
  const alertCreateTranscriptFromRecording = useUISlice(
    (state) => state.alertCreateTranscriptFromRecording,
  );
  const alertWidgetSearch = useUISlice((state) => state.alertWidgetSearch);
  const alertLanguagePicker = useUISlice((state) => state.alertLanguagePicker);
  const alertModal = useUISlice((state) => state.alertModal);
  const alertPrompt = useUISlice((state) => state.alertPrompt);
  const alertPromptSignIn = useUISlice((state) => state.alertPromptSignIn);
  const alertTagPicker = useUISlice((state) => state.alertTagPicker);

  const setSelectedTagFilter = useAppStore(
    (state) => state.setSelectedTagFilter,
  );
  const allTags = useMemo(() => userData?.allTags || [], [userData?.allTags]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [journeyDictationSurfaceOpen, setJourneyDictationSurfaceOpen] =
    useState(false);
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState<
    Set<string>
  >(new Set());
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(
    transcripts.length === 0,
  );
  const uploadEntry = isUploadEntry(
    window.location.search,
  );
  const [entryReady, setEntryReady] = useState(!uploadEntry);
  const openedUploadEntry = useRef(false);
  const isSelectionMode = selectedTranscriptIds.size > 0;

  const openSettings = (section?: "dictation") => {
    const hash = section === "dictation" ? "#dictation-settings" : "";
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}${hash}`,
    );
    setIsSettingsOpen(true);
  };

  const closeSettings = () => {
    if (window.location.hash === "#dictation-settings") {
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${window.location.search}`,
      );
    }
    setIsSettingsOpen(false);
  };

  const openDictationSettings = () => {
    if (!desktop.isAvailable) {
      openSettings("dictation");
      return;
    }
    useUISlice.setState({ desktopDictationSettingsOpen: true });
  };

  // A file the OS handed to the app ("Open with 1Transcribe") — route it
  // into the regular import flow once home is up.
  const pendingSharedFile = useUISlice((state) => state.pendingSharedFile);
  const desktopUploadRequested = useUISlice(
    (state) => state.desktopUploadRequested,
  );
  useEffect(() => {
    if (!desktopUploadRequested || isInitialLoading) return;
    useUISlice.setState({ desktopUploadRequested: false });
    openFileImport();
    // Uses the same import sheet, language choice, and multi-file workflow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktopUploadRequested, isInitialLoading]);
  useEffect(() => {
    if (!pendingSharedFile || isInitialLoading) return;
    useUISlice.getState().setPendingSharedFile(undefined);
    openFileImport({
      name: pendingSharedFile.name,
      size: pendingSharedFile.size ?? 0,
      type: pendingSharedFile.mimeType ?? "",
      path: pendingSharedFile.uri,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSharedFile, isInitialLoading]);

  const loadMoreTranscripts = async () => {
    if (!transcriptsNextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      await actions.transcript.list(transcriptsNextCursor);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const tagsByName = useMemo(() => {
    const map = new Map<string, Tag>();
    for (const tag of allTags) map.set(tag.name, tag);
    return map;
  }, [allTags]);

  const getTagsForTranscript = useCallback(
    (tagsIds?: string[]) => {
      if (!tagsIds?.length) return [];
      const result: Tag[] = [];
      for (const name of tagsIds) {
        const tag = tagsByName.get(name);
        if (tag) result.push(tag);
      }
      return result;
    },
    [tagsByName],
  );

  // Filter transcripts by selected tag (system or user tag)
  const filteredTranscripts = useMemo(() => {
    // "All" - show all transcripts
    if (!selectedTagFilter) return transcripts;

    // User tags - filter by tag name in tagsIds
    return transcripts.filter((transcript) =>
      transcript.tagsIds?.includes(selectedTagFilter),
    );
  }, [transcripts, selectedTagFilter]);
  const hasVisibleTranscripts = filteredTranscripts.length > 0;

  const enterSelectionMode = useCallback(() => {
    const firstVisibleTranscript = filteredTranscripts[0];
    if (firstVisibleTranscript) {
      setSelectedTranscriptIds(new Set([firstVisibleTranscript.id]));
    }
  }, [filteredTranscripts]);

  // Fetch missing transcripts when a tag filter is selected
  useEffect(() => {
    if (selectedTagFilter) {
      void actions.tag.fetchMissingTranscriptsForTag(selectedTagFilter);
    }
  }, [selectedTagFilter]);

  // Fall back to all transcripts if the selected tag no longer exists
  useEffect(() => {
    if (
      selectedTagFilter &&
      !allTags.some((tag) => tag.name === selectedTagFilter)
    ) {
      setSelectedTagFilter(null);
    }
  }, [selectedTagFilter, allTags, setSelectedTagFilter]);

  useEffect(() => {
    if (selectedTranscriptIds.size === 0) return;

    const visibleTranscriptIds = new Set(filteredTranscripts.map((t) => t.id));
    const nextSelectedIds = Array.from(selectedTranscriptIds).filter((id) =>
      visibleTranscriptIds.has(id),
    );

    if (nextSelectedIds.length !== selectedTranscriptIds.size) {
      setSelectedTranscriptIds(new Set(nextSelectedIds));
    }
  }, [filteredTranscripts, selectedTranscriptIds]);

  // Web-specific back button handling using browser history
  useEffect(() => {
    const handlePopState = () => {
      if (isSelectionMode) {
        exitSelectionMode();
        // Push state back to prevent actual navigation
        window.history.pushState(null, "", window.location.href);
        return;
      }

      if (alertLanguagePicker) {
        useUISlice.getState().setAlertLanguagePicker(undefined);
        window.history.pushState(null, "", window.location.href);
        return;
      }

      if (alertWidgetCreateTranscriptFromFileImport) {
        if (uploadEntry) return;
        useUISlice
          .getState()
          .setAlertCreateWidgetTranscriptFromFileImport(undefined);
        window.history.pushState(null, "", window.location.href);
        return;
      }

      if (alertCreateTranscriptFromRecording) {
        useUISlice.getState().setAlertCreateTranscriptFromRecording(undefined);
        window.history.pushState(null, "", window.location.href);
        return;
      }

      if (alertWidgetSearch) {
        useUISlice.getState().setAlertWidgetSearch(undefined);
        window.history.pushState(null, "", window.location.href);
        return;
      }

      if (alertPaywallVisible) {
        useUISlice.getState().setPaywallVisible(false);
        window.history.pushState(null, "", window.location.href);
        return;
      }
    };

    /* action-id: lifecycle.browser.back */ window.addEventListener(
      "popstate",
      handlePopState,
    );

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [
    isSelectionMode,
    alertWidgetCreateTranscriptFromFileImport,
    alertCreateTranscriptFromRecording,
    alertPaywallVisible,
    alertLanguagePicker,
    alertWidgetSearch,
    uploadEntry,
  ]);

  useHomeBootstrap(setIsInitialLoading, uploadEntry);

  useLayoutEffect(() => {
    if (!uploadEntry || openedUploadEntry.current) return;
    openedUploadEntry.current = true;
    /* action-id: lifecycle.uploadEntry */ openFileImport(
      undefined,
      isInitialLoading,
      true,
    );
    setEntryReady(true);
    // First funnel step for ad links; home's own button logs importSelected.
    logClientEvent("import.viewed", { source: "entry" });
    // Mount before paint so an ad click never exposes Home first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadEntry]);

  useEffect(() => {
    if (!uploadEntry || isInitialLoading) return;
    const importFlow =
      useUISlice.getState().alertWidgetCreateTranscriptFromFileImport;
    if (!importFlow?.loading) return;
    useUISlice.getState().setAlertCreateWidgetTranscriptFromFileImport({
      ...importFlow,
      loading: false,
    });
  }, [isInitialLoading, uploadEntry]);

  const pendingTranscriptIds = useMemo(
    () =>
      transcripts
        .filter(isPendingTranscript)
        .map((transcript) => transcript.id)
        .join("\n"),
    [transcripts],
  );

  // Home owns the visible pending cards. Reuse the shared, deduplicated poller
  // so each card updates as soon as its server transcript becomes terminal.
  useEffect(() => {
    if (isInitialLoading || !pendingTranscriptIds) return;
    startPendingTranscriptPolls();
  }, [isInitialLoading, pendingTranscriptIds]);

  // Polls are bounded by the action's give-up policy. Re-enter that policy
  // whenever an installed app resumes or a browser tab becomes visible again.
  useEffect(() => {
    if (isInitialLoading) return;

    if (!runtime.isNative) {
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          startPendingTranscriptPolls();
        }
      };
      // action-id: lifecycle.app.foreground
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () =>
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
    }

    let disposed = false;
    let listener: { remove: () => Promise<void> } | undefined;
    void runtime.app
      .onStateChange(({ isActive }) => {
        if (isActive) startPendingTranscriptPolls();
      })
      .then((handle) => {
        if (disposed) void handle.remove();
        else listener = handle;
      });
    return () => {
      disposed = true;
      void listener?.remove();
    };
  }, [isInitialLoading]);

  const exitSelectionMode = () => {
    setSelectedTranscriptIds(new Set());
  };

  const toggleSelection = (transcriptId: string) => {
    const newSelected = new Set(selectedTranscriptIds);
    if (newSelected.has(transcriptId)) {
      newSelected.delete(transcriptId);
    } else {
      newSelected.add(transcriptId);
    }
    setSelectedTranscriptIds(newSelected);
  };

  const handleTranscriptClick = (transcriptId: string) => {
    if (isSelectionMode) {
      toggleSelection(transcriptId);
    } else {
      router.push({
        pathname: "/transcript",
        search: `?${new URLSearchParams({ id: transcriptId })}`,
      });
    }
  };

  const handleTranscriptLongPress = (transcriptId: string) => {
    if (!isSelectionMode) {
      // Enter selection mode with this transcript selected
      setSelectedTranscriptIds(new Set([transcriptId]));
    }
  };

  const deleteSelectedTranscripts = () => {
    useUISlice.getState().setAlertModal({
      title: t("home.deleteTranscriptsTitle"),
      subtitle: t("home.deleteTranscriptsSubtitle", {
        count: selectedTranscriptIds.size,
      }),
      cancelText: t("home.cancel"),
      button: {
        text: t("home.delete"),
        destructive: true,
        onPress: async () => {
          const idsToDelete = Array.from(selectedTranscriptIds);

          // Clear selection first
          exitSelectionMode();
          useUISlice.getState().setAlertModal(undefined);

          // Delete sequentially (await) — each delete recomputes and writes
          // allTags, so firing them concurrently let a stale response clobber
          // a later one and resurrect removed tags.
          const failedIds: string[] = [];
          for (const id of idsToDelete) {
            const result = await actions.transcript.delete(id);
            if ("error" in result) failedIds.push(id);
          }
          if (failedIds.length) {
            setSelectedTranscriptIds(
              (current) => new Set([...current, ...failedIds]),
            );
            toast.error(t("useAuth.signInFailed"));
          }
          if (failedIds.length < idsToDelete.length) {
            toast.success(t("home.deleteTranscriptsSuccess"));
          }
        },
      },
    });
  };

  const downloadSelectedTranscripts = () => {
    const selected = transcripts.filter(
      (transcript) =>
        selectedTranscriptIds.has(transcript.id) &&
        transcript.segments.length > 0,
    );
    if (selected.length === 0) return;
    useUISlice.getState().setAlertExportTranscriptOptions({
      hasSpeakers: selected.some((transcript) =>
        transcript.segments.some((segment) => segment.speaker),
      ),
      onExport: async (options) => {
        const result = await exportTranscriptArchive(selected, options);
        if (result.succeeded === 0) {
          toast.error(t("actions.exportBulkError"));
        } else if (result.failed > 0) {
          toast(
            t("actions.exportBulkPartial", {
              count: result.succeeded,
              failed: result.failed,
            }),
          );
        } else {
          toast.success(
            t("actions.exportBulkSuccess", {
              count: result.succeeded,
            }),
          );
        }
        exitSelectionMode();
      },
    });
  };

  const addTagsToSelected = () => {
    useUISlice.getState().setAlertTagPicker({
      transcriptIds: Array.from(selectedTranscriptIds),
      onComplete: () => exitSelectionMode(),
    });
  };

  const openFileImport = (
    preselectedFile?: {
      name: string;
      size: number;
      type: string;
      path: string;
    },
    loading = false,
    openedByCampaign = false,
  ) => {
    if (!openedByCampaign) logClientEvent("home.importSelected");
    useUISlice.getState().setAlertCreateWidgetTranscriptFromFileImport({
      loading,
      preselectedFile,
      onImport: async ({
        file,
        isAudioOrVideoFile,
        languageCode,
        downloadUrl,
      }) => {
        const created = await actions.transcript.create({
          type: isAudioOrVideoFile
            ? "audio_or_video_import"
            : "take_photo_or_import_image_or_pdf",
          originalFileUrl: downloadUrl,
          language: languageCode,
          fileName: file.name,
        });
        if ("error" in created) {
          throw createActionError(created.error);
        }
        return created.value.id;
      },
      onImported: async ({ transcriptIds, totalFiles }) => {
        if (transcriptIds.length === 0) return;
        if (totalFiles === 1 && transcriptIds.length === 1) {
          const search = new URLSearchParams({ id: transcriptIds[0] });
          if (uploadEntry) {
            search.set("entry", UPLOAD_ENTRY);
          }
          router.push({
            pathname: "/transcript",
            search: `?${search}`,
          });
          return;
        }
        useUISlice
          .getState()
          .setAlertCreateWidgetTranscriptFromFileImport(undefined);
      },
    });
  };

  const formatTime = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, []);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }, []);

  return (
    <Container
      className={`h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col overflow-hidden font-sans relative selection:bg-[var(--accent)]/20 select-none ${
        !entryReady
          ? "invisible"
          : ""
      }`}
    >
      <style>{`
        @keyframes click-animation {
          0%, 100% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(0.9) rotate(-15deg); }
          50% { transform: scale(1) rotate(0deg); }
        }
        .animate-click {
          animation: click-animation 1.5s infinite ease-in-out;
        }
      `}</style>

      {/* Background Ambience */}
      <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-[var(--ambience-blue)] rounded-full blur-[60px] opacity-60 pointer-events-none will-change-transform" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[var(--ambience-purple)] rounded-full blur-[50px] opacity-60 pointer-events-none will-change-transform" />

      {/* Main Content Area */}
      <div className="flex flex-col h-full max-w-7xl mx-auto w-full relative z-10">
        {/* Mobile Header (Native-like) */}
        <div className="md:hidden shrink-0 z-10 relative">
          <header className="px-5 py-3 flex items-center justify-between relative">
            {/* Select Button */}
            {hasVisibleTranscripts && (
              <button
                data-action-id="home.selection.toggle"
                onClick={() => isSelectionMode ? exitSelectionMode() : enterSelectionMode()}
                className={`icon-btn ${isSelectionMode ? "bg-[var(--glass-bg-active)]" : ""}`}
              >
                <SelectIcon
                  className={`transition-colors text-[var(--text-primary)] ${isSelectionMode ? "" : "opacity-80"}`}
                  size={20}
                />
              </button>
            )}

            {/* Right Icons */}
            <div className="ml-auto flex items-center gap-2">
              <div className="glass flex items-center gap-1 rounded-full p-1">
                {!isSubscribed ? (
                  <button
                    data-action-id="home.paywall.open"
                    onClick={() =>
                      useUISlice
                        .getState()
                        .setPaywallVisible({ utmSource: "home.banner" })
                    }
                    className="h-11 pl-4 pr-3 rounded-full flex items-center justify-center active:scale-95 transition-all bg-gradient-to-r from-[var(--premium-cta-start)] to-[var(--premium-cta-end)] shadow-[0_8px_20px_-10px_var(--premium-cta-glow)] relative"
                  >
                    <CrownIcon
                      size={16}
                      className="text-[var(--premium-cta-text)] mr-1.5"
                    />
                    <span className="text-[var(--premium-cta-text)] font-bold text-xs tracking-wider uppercase mr-1.5">
                      {t("home.upgrade")}
                    </span>
                    <CursorIcon
                      size={16}
                      className="text-[var(--premium-cta-text)]"
                    />
                  </button>
                ) : (
                  <HomeBillingButton
                    label={t("home.billing")}
                    variant="compact"
                  />
                )}
                <button
                  data-action-id="home.search.open"
                  onClick={() => useUISlice.getState().setAlertWidgetSearch({})}
                  className="icon-btn icon-btn-quiet text-[var(--text-primary)]"
                >
                  <SearchIcon size={20} className="opacity-80" />
                </button>
                <button
                  data-action-id="home.settings.open"
                  onClick={() => openSettings()}
                  className="icon-btn icon-btn-quiet text-[var(--text-primary)]"
                >
                  <SettingsIcon
                    size={20}
                    className="opacity-80 transition-colors"
                  />
                </button>
              </div>
            </div>
          </header>

          {/* Tags Filter Bar */}
          {allTags.length > 0 && (
            <TagFilterBar
              tags={allTags}
              selectedTagId={selectedTagFilter}
              onSelectTag={setSelectedTagFilter}
            />
          )}
        </div>

        {/* Header Section (Desktop) */}
        <div className="hidden md:flex absolute top-0 left-0 right-0 z-20 pt-6 px-6 pb-2 justify-center pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-2">
            <header className="glass flex h-16 items-center gap-2 rounded-full p-2">
              {/* Left Ticker / Selection */}
              {hasVisibleTranscripts && (
                <>
                  <button
                    data-action-id="home.selection.toggle"
                    onClick={() => isSelectionMode ? exitSelectionMode() : enterSelectionMode()}
                    className={`icon-btn icon-btn-quiet ${isSelectionMode ? "bg-[var(--accent)] text-[var(--text-inverse)] hover:bg-[var(--accent)]" : "text-[var(--text-primary)]"}`}
                  >
                    <SelectIcon size={20} />
                  </button>
                  <div className="w-px h-5 bg-[var(--hairline)] mx-1" />
                </>
              )}

              {allTags.length > 0 && (
                <>
                  <TagFilterBar
                    tags={allTags}
                    selectedTagId={selectedTagFilter}
                    onSelectTag={setSelectedTagFilter}
                    className="!p-0 !px-1"
                  />
                  <div className="w-px h-5 bg-[var(--hairline)] mx-1" />
                </>
              )}

              {!isSubscribed ? (
                <button
                  data-action-id="home.paywall.open"
                  onClick={() =>
                    useUISlice
                      .getState()
                      .setPaywallVisible({ utmSource: "home.banner" })
                  }
                  className="h-11 pl-4 pr-3 rounded-full flex items-center justify-center active:scale-95 transition-all bg-gradient-to-r from-[var(--premium-cta-start)] to-[var(--premium-cta-end)] shadow-[0_8px_20px_-10px_var(--premium-cta-glow)] relative mx-1"
                >
                  <CrownIcon
                    size={16}
                    className="text-[var(--premium-cta-text)] mr-1.5"
                  />
                  <span className="text-[var(--premium-cta-text)] font-bold text-xs tracking-wider uppercase mr-1.5">
                    {t("home.upgrade")}
                  </span>
                  <CursorIcon
                    size={16}
                    className="text-[var(--premium-cta-text)]"
                  />
                </button>
              ) : (
                <HomeBillingButton
                  label={t("home.billing")}
                  variant="desktop"
                />
              )}

              <button
                data-action-id="home.search.open"
                onClick={() => useUISlice.getState().setAlertWidgetSearch({})}
                className="icon-btn icon-btn-quiet text-[var(--text-primary)]"
              >
                <SearchIcon size={20} className="opacity-80" />
              </button>

              <button
                data-action-id="home.settings.open"
                onClick={() => openSettings()}
                className="icon-btn icon-btn-quiet text-[var(--text-primary)]"
              >
                <SettingsIcon size={20} />
              </button>
            </header>
          </div>
        </div>

        {/* Scrollable Content */}
        <main className="flex-1 overflow-y-auto px-3 md:px-6 pb-36 pt-4 md:pt-28 scrollbar-hide">
          <div className="mx-auto w-full max-w-lg">
            <HomeNextStep
              onDictationSurfaceChange={setJourneyDictationSurfaceOpen}
            />
          </div>

          <div
            data-testid="home-transcript-grid"
            className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4"
          >
            {filteredTranscripts.map((transcript) => (
              <TranscriptCard
                actionId="home.transcript.activate"
                key={transcript.id}
                date={formatDate(transcript.createdAt)}
                time={formatTime(transcript.createdAt)}
                title={transcript.fileName || t("home.untitled")}
                preview={
                  transcript.segments?.map((s) => s.text).join(" ") || ""
                }
                direction={getTranscriptDirection(transcript)}
                tags={getTagsForTranscript(transcript.tagsIds)}
                status={transcript.status}
                isSelected={selectedTranscriptIds.has(transcript.id)}
                isSelectionMode={isSelectionMode}
                onClick={() => handleTranscriptClick(transcript.id)}
                onLongPress={() => handleTranscriptLongPress(transcript.id)}
              />
            ))}
          </div>

          {transcriptsNextCursor && !selectedTagFilter && (
            <div className="flex justify-center mt-12 mb-8">
              <button
                data-action-id="home.transcripts.loadMore"
                onClick={loadMoreTranscripts}
                disabled={isLoadingMore}
                className="btn btn-glass text-sm"
              >
                {isLoadingMore ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-[var(--text-secondary)] border-t-transparent rounded-full" />
                    {t("home.loading")}
                  </>
                ) : (
                  t("home.loadMore")
                )}
              </button>
            </div>
          )}

          {transcripts.length === 0 && isInitialLoading && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div
                  key={i}
                  className="glass-tile w-full p-5 md:aspect-[6/5] md:min-h-56 rounded-3xl md:rounded-[32px] flex flex-col gap-3 animate-pulse"
                >
                  <div className="h-3 w-2/5 rounded-full bg-[var(--fill-2)]" />
                  <div className="h-4 w-4/5 rounded-full bg-[var(--fill-2)]" />
                  <div className="space-y-2 mt-1">
                    <div className="h-2.5 w-full rounded-full bg-[var(--fill-2)]" />
                    <div className="h-2.5 w-3/4 rounded-full bg-[var(--fill-2)]" />
                    <div className="h-2.5 w-1/2 rounded-full bg-[var(--fill-2)]" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {transcripts.length === 0 && !isInitialLoading && (
            <div
              data-testid="home-empty-guidance"
              className="flex min-h-[58vh] flex-col items-center justify-center px-6 pb-6 pt-16"
            >
              <div className="flex flex-col items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
                <div className="max-w-[280px] px-6 py-4 text-center">
                  <p className="text-sm font-medium leading-relaxed text-[var(--text-secondary)]">
                    {t("home.tapToCreate")}
                  </p>
                </div>

                <div className="flex flex-col items-center gap-2 animate-bounce motion-reduce:animate-none opacity-40">
                  <ArrowDownIcon
                    size={28}
                    className="text-[var(--text-secondary)]"
                  />
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Floating Bottom Record Button (Desktop) */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 hidden px-6 pb-10 pt-8 md:block">
        <div
          data-testid="home-desktop-action-scrim"
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[var(--bg-primary)] via-[var(--bg-primary)] via-40% to-transparent backdrop-blur-sm"
        />
        <div className="relative flex justify-center">
          {isSelectionMode ? (
            <div className="glass-thick pointer-events-auto rounded-full p-2 pl-5 flex items-center gap-2 transform transition-all duration-300">
              <span className="text-[var(--text-primary)] font-medium text-sm mr-4">
                {t("home.selected", { count: selectedTranscriptIds.size })}
              </span>
              <button
                data-action-id="home.selection.tags"
                onClick={addTagsToSelected}
                className="icon-btn icon-btn-quiet bg-[var(--accent-bg)] text-[var(--text-primary)]"
              >
                <TagIcon size={18} />
              </button>
              <button
                data-action-id="home.selection.download"
                onClick={downloadSelectedTranscripts}
                className="icon-btn icon-btn-quiet"
              >
                <DownloadIcon size={18} />
              </button>
              <button
                data-action-id="home.selection.delete"
                onClick={deleteSelectedTranscripts}
                className="icon-btn icon-btn-quiet bg-[var(--destructive-bg)] text-[var(--destructive)] hover:text-[var(--destructive)]"
              >
                <TrashIcon size={18} />
              </button>
              <div className="w-px h-6 bg-[var(--hairline)] mx-1" />
              <button
                data-action-id="home.selection.exit"
                onClick={exitSelectionMode}
                className="icon-btn icon-btn-quiet"
              >
                <CloseIcon size={20} />
              </button>
            </div>
          ) : (
            <div className="pointer-events-auto flex items-center gap-4">
              {/* Import Button */}
              <button
                data-action-id="home.import"
                onClick={() => openFileImport()}
                className="glass glass-raise h-[68px] pl-2 pr-7 rounded-full flex items-center gap-3 active:scale-95 group"
              >
                <div className="w-13 h-13 rounded-full bg-[var(--fill-2)] flex items-center justify-center">
                  <UploadIcon
                    className="text-[var(--text-primary)] opacity-80 group-hover:opacity-100 transition-colors"
                    size={22}
                  />
                </div>
                <span className="text-[var(--text-primary)] font-bold text-lg">
                  {t("home.import")}
                </span>
              </button>

              {/* Record Button — the one solid control on the page. */}
              <button
                data-action-id="home.record"
                onClick={() =>
                  useUISlice
                    .getState()
                    .setAlertCreateTranscriptFromRecording(true)
                }
                className="h-[68px] pl-2 pr-7 rounded-full bg-[var(--accent)] flex items-center gap-3 shadow-[0_24px_48px_-22px_var(--accent-glow)] hover:-translate-y-0.5 active:scale-95 transition-all duration-300 group"
              >
                <div className="w-13 h-13 rounded-full bg-[#FF3B30] flex items-center justify-center">
                  <div className="w-4 h-4 rounded-full bg-white" />
                </div>
                <span className="text-[var(--text-inverse)] font-bold text-lg">
                  {t("home.startRecording")}
                </span>
              </button>

              {/* Dictation settings */}
              <button
                data-action-id="home.dictation.openSettings"
                onClick={openDictationSettings}
                className="glass glass-raise h-[68px] pl-2 pr-6 rounded-full flex items-center gap-3 active:scale-95 group"
              >
                <div className="w-13 h-13 rounded-full bg-[var(--fill-2)] flex items-center justify-center">
                  <AudioWaveformIcon
                    size={22}
                    className="text-[var(--text-primary)] opacity-80"
                  />
                </div>
                <span className="text-[var(--text-primary)] font-bold text-lg">
                  {t("webDictation.dictate", { defaultValue: "Dictate" })}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation (Native-like) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-6 px-6 z-20 pointer-events-none">
        {/* Gradient Fade */}
        <div
          data-testid="home-mobile-action-scrim"
          className="absolute inset-0 bg-gradient-to-t from-[var(--bg-primary)] via-[var(--bg-primary)] via-40% to-transparent backdrop-blur-sm pointer-events-none"
        />

        <div className="relative pointer-events-auto w-full">
          {isSelectionMode ? (
            <div className="max-w-md mx-auto">
              <div className="glass-thick rounded-full px-2 py-2 animate-in slide-in-from-bottom-10 duration-300 flex items-center justify-between">
                <div className="flex items-center gap-2 pl-1">
                  <button
                    data-action-id="home.selection.exit"
                    onClick={exitSelectionMode}
                    className="icon-btn icon-btn-quiet"
                  >
                    <CloseIcon size={18} />
                  </button>
                  <span className="text-[var(--text-primary)] font-medium text-sm">
                    {t("home.selected", { count: selectedTranscriptIds.size })}
                  </span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    data-action-id="home.selection.tags"
                    onClick={addTagsToSelected}
                    className="flex items-center gap-2 h-11 px-4 rounded-full bg-[var(--accent)] text-[var(--text-inverse)] font-medium transition-transform shadow-[0_12px_24px_-14px_var(--accent-glow)] active:scale-95"
                  >
                    <TagIcon size={16} />
                    <span className="text-sm">{t("home.tags")}</span>
                  </button>
                  <button
                    data-action-id="home.selection.download"
                    onClick={downloadSelectedTranscripts}
                    className="icon-btn icon-btn-quiet"
                  >
                    <DownloadIcon size={18} />
                  </button>
                  <button
                    data-action-id="home.selection.delete"
                    onClick={deleteSelectedTranscripts}
                    className="icon-btn icon-btn-quiet active:bg-[var(--destructive-bg)] active:text-[var(--destructive)]"
                  >
                    <TrashIcon size={18} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full">
              {/* Left Group */}
              <div className="flex items-center gap-6">
                {/* Import Button */}
                <button
                  data-action-id="home.import"
                  onClick={() => openFileImport()}
                  className="flex flex-col items-center gap-1.5 group"
                >
                  <div className="glass w-16 h-16 rounded-full flex items-center justify-center active:bg-[var(--glass-bg-active)] active:scale-90 transition-all duration-300">
                    <UploadIcon
                      className="text-[var(--text-primary)] opacity-80 group-active:text-[var(--text-primary)] group-active:opacity-100 transition-colors"
                      size={24}
                    />
                  </div>
                  <span className="text-[11px] font-medium text-[var(--text-tertiary)] group-active:text-[var(--text-secondary)] transition-colors">
                    {t("home.import")}
                  </span>
                </button>

                {/* Record Button */}
                <button
                  data-action-id="home.record"
                  onClick={() =>
                    useUISlice
                      .getState()
                      .setAlertCreateTranscriptFromRecording(true)
                  }
                  className="flex flex-col items-center gap-1.5 group"
                >
                  <div className="glass w-16 h-16 rounded-full flex items-center justify-center active:bg-[var(--glass-bg-active)] active:scale-90 transition-all duration-300">
                    <MicrophoneIcon
                      className="text-[var(--text-primary)] opacity-80 group-active:text-[var(--text-primary)] group-active:opacity-100 transition-colors"
                      size={24}
                    />
                  </div>
                  <span className="text-[11px] font-medium text-[var(--text-tertiary)] group-active:text-[var(--text-secondary)] transition-colors">
                    {t("home.record")}
                  </span>
                </button>

                <NativeHomeScanButton />
              </div>

              {/* Right Group - Dictation settings */}
              <button
                data-action-id="home.dictation.openSettings"
                onClick={openDictationSettings}
                className="flex flex-col items-center gap-1.5 group"
              >
                <div className="glass w-16 h-16 rounded-full flex items-center justify-center active:bg-[var(--glass-bg-active)] active:scale-90 transition-all duration-300">
                  <AudioWaveformIcon
                    className="text-[var(--text-primary)] opacity-80 group-active:text-[var(--text-primary)] group-active:opacity-100 transition-colors"
                    size={24}
                  />
                </div>
                <span className="text-[11px] font-medium text-[var(--text-tertiary)] group-active:text-[var(--text-secondary)] transition-colors">
                  {t("webDictation.dictate", { defaultValue: "Dictate" })}
                </span>
              </button>
            </div>
          )}
        </div>
      </nav>

      <HomeNativeFeatures
        isSelectionMode={isSelectionMode}
        exitSelectionMode={exitSelectionMode}
        settingsOpen={isSettingsOpen}
        journeyDictationSurfaceOpen={journeyDictationSurfaceOpen}
      />

      {/* Settings Slide-over */}
      <div
        className={`fixed inset-0 z-50 transition-all duration-300 ${
          isSettingsOpen ? "visible" : "invisible delay-300"
        }`}
      >
        {/* Backdrop */}
        <div
          data-action-id="home.settings.close"
          className={`scrim absolute inset-0 transition-opacity duration-300 ${
            isSettingsOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={closeSettings}
        />

        {/* Panel */}
        <div
          className={`absolute top-0 end-0 h-full w-full max-w-md bg-[var(--bg-primary)] shadow-2xl transition-transform duration-300 ease-out transform ${
            isSettingsOpen
              ? "translate-x-0"
              : "translate-x-full rtl:-translate-x-full"
          }`}
        >
          <Settings isOpen={isSettingsOpen} onClose={closeSettings} />
        </div>
      </div>
    </Container>
  );
};

// Transcript Card Component
const TranscriptCard = memo(
  ({
    actionId,
    time,
    date,
    title,
    preview,
    direction,
    tags,
    status,
    isSelected,
    isSelectionMode,
    onClick,
    onLongPress,
  }: {
    actionId: string;
    time: string;
    date: string;
    title: string;
    preview: string;
    direction: ContentDirection;
    tags?: Tag[];
    status?: Transcript["status"];
    isSelected?: boolean;
    isSelectionMode?: boolean;
    onClick: () => void;
    onLongPress?: () => void;
  }) => {
    const { t } = useTranslation();
    const [isLongPress, setIsLongPress] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const isFailed = status === "transcription_failed";
    const isPending =
      status === "transcription_started" ||
      status === "upload_started" ||
      status === "upload_completed";

    const handleStart = () => {
      setIsLongPress(false);
      timerRef.current = setTimeout(() => {
        setIsLongPress(true);
        if (onLongPress) onLongPress();
      }, 500);
    };

    const handleEnd = (e: any) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (isLongPress) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Remove file extension from title
    const displayTitle = title.replace(/\.[^/.]+$/, "");

    return (
      <div
        data-testid="home-transcript-card"
        data-action-id={actionId}
        role="button"
        aria-pressed={isSelectionMode ? isSelected : undefined}
        tabIndex={0}
        onClick={(e) => {
          if (isLongPress) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          onClick();
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        onMouseDown={handleStart}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchEnd={handleEnd}
        onContextMenu={(e) => {
          e.preventDefault();
          if (onLongPress) onLongPress();
        }}
        className={`glass-tile w-full md:aspect-[6/5] md:min-h-56 p-5 rounded-3xl md:rounded-[32px] flex flex-col items-start group relative overflow-hidden ${
          isSelected ? "selected-surface" : "glass-raise"
        }`}
      >
        {/* Background Gradient for depth */}
        {!isSelected && (
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--fill-1)] to-transparent opacity-60 group-hover:opacity-100 transition-opacity pointer-events-none" />
        )}

        {/* Failed status glow effect */}
        {isFailed && !isSelected && (
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--destructive-bg)] via-transparent to-transparent pointer-events-none" />
        )}

        {!isSelected && !isSelectionMode && (
          <button
            data-action-id="home.transcript.select"
            className="absolute top-3 end-3 md:top-4 md:end-4 w-9 h-9 rounded-full bg-[var(--fill-2)] flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-all duration-200 z-10 hover:bg-[var(--accent)] hover:text-[var(--text-inverse)]"
            onClick={(e) => {
              e.stopPropagation();
              onLongPress && onLongPress();
            }}
          >
            <SelectIcon size={14} />
          </button>
        )}

        {isSelected && (
          <div className="absolute top-3 end-3 md:top-4 md:end-4 w-9 h-9 bg-[var(--accent)] rounded-full flex items-center justify-center shadow-[0_10px_20px_-12px_var(--accent-glow)] animate-in fade-in zoom-in duration-200 z-10">
            <CheckIcon
              size={16}
              className="text-[var(--text-inverse)] stroke-[3]"
            />
          </div>
        )}
        {!isSelected && isSelectionMode && (
          <div className="absolute top-3 end-3 md:top-4 md:end-4 w-9 h-9 rounded-full bg-[var(--fill-1)] shadow-[inset_0_0_0_2px_var(--fill-3)] z-10" />
        )}
        {isFailed && !isSelected && !isSelectionMode && (
          <div className="absolute top-3 end-3 md:top-4 md:end-4 w-10 h-10 rounded-full bg-[var(--destructive-bg)] flex items-center justify-center z-10">
            <WarningIcon size={20} className="text-[var(--destructive)]" />
          </div>
        )}

        <div className="w-full flex justify-between items-start mb-3 md:mb-4 relative z-0">
          <span
            className={`text-[10px] md:text-xs font-bold tracking-wider md:tracking-widest uppercase opacity-60 ${
              isSelected
                ? "text-[var(--text-secondary)]"
                : isFailed
                  ? "text-[var(--destructive)]"
                  : "text-[var(--text-secondary)]"
            }`}
          >
            {date} • {time}
          </span>
        </div>

        <h3
          data-testid="home-transcript-title"
          dir="auto"
          className="mb-2 w-full shrink-0 line-clamp-2 [overflow-wrap:anywhere] text-start text-[15px] font-medium leading-tight text-[var(--text-primary)] transition-colors md:text-xl md:font-semibold"
        >
          {displayTitle}
        </h3>

        {isPending && !isSelected ? (
          <div className="w-full flex flex-col gap-3 mt-1">
            <div className="flex flex-col gap-2">
              <div className="h-2.5 w-full rounded-full bg-[var(--fill-2)] animate-pulse" />
              <div className="h-2.5 w-4/5 rounded-full bg-[var(--fill-2)] animate-pulse [animation-delay:150ms]" />
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-2 h-2 rounded-full bg-[var(--text-tertiary)] animate-pulse" />
              <span className="text-xs font-medium text-[var(--text-muted)]">
                {status === "upload_started"
                  ? t("home.uploading")
                  : t("home.transcribing")}
              </span>
            </div>
          </div>
        ) : (
          <>
            <p
              data-testid="home-transcript-preview"
              dir={direction}
              className={`text-[13px] md:text-base text-[var(--text-tertiary)] leading-relaxed text-start ${
                tags && tags.length > 0
                  ? "line-clamp-4 md:line-clamp-2"
                  : "line-clamp-4 md:line-clamp-3"
              }`}
            >
              {preview}
            </p>

            {tags && tags.length > 0 && (
              <div className="mt-auto pt-3 w-full">
                <TagDots tags={tags} />
              </div>
            )}
          </>
        )}
      </div>
    );
  },
);

export default Home;
